import { AxIconWrapper, AxInput, AxTable } from "@dynata/axiom3";
import { IGetAllProjectsResponseModel } from "@lib/types";
import { ChangeEvent, ReactNode, useState } from "react";
import { useToggleFavoriteProject, useUpdateProject } from "hooks/projects";
import { UseMutateAsyncFunction } from "@tanstack/react-query";

import 'features/ad-hoc-projects/projects-table.scss'
import { Link } from "react-router-dom";
import paths from "paths";

// https://ant.design/components/table#column
interface ITableColumn {
  title: string;
  dataIndex: string;
  key: string;
  className?: string;
  render?: (text: string, record: IProjectsTableData) => ReactNode
}

export interface IProjectsTableData extends IGetAllProjectsResponseModel {
  isFavorite: boolean
}

interface IProjectInputProps {
  accountNumber: number
  projectId: number
  value: string
}

function ProjectDescription({value, projectId, accountNumber}: IProjectInputProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [text, setText] = useState(value)
  const { mutateAsync } = useUpdateProject(accountNumber, projectId)


  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  }

  const handleSubmit = async () => {
    await mutateAsync({ description: text })
    setIsEditing(false)
  }

  return isEditing ?
    <AxInput 
      autoFocus 
      type="default" 
      size="small" 
      placeholder="Description" 
      value={text} 
      onChange={handleChange}
      onPressEnter={handleSubmit} 
      onBlur={handleSubmit} />
    :
    <div className="project-description" onClick={() => setIsEditing(true)}>
      {value}
      <div className="edit-icon">
        <AxIconWrapper icon="fa-pen"/>
      </div>
    </div>
}

function getColumns({
  toggleFavorite,
  accountNumber,
}: {
  toggleFavorite: UseMutateAsyncFunction<void, unknown, {projectId: number, isFavorite: boolean}, unknown>
  accountNumber: number,
}) {
  return new Array<ITableColumn>({
    render: (_, record) => (
      <AxIconWrapper
        onClick={async () => {
          await toggleFavorite({projectId: record.id, isFavorite: record.isFavorite})
        }}
        type={record.isFavorite ? "solid" : "light"}
        icon="fa-star"
        size="20"
      />
    ),
    dataIndex: "isFavorite",
    title: "Favorite",
    key: "favorite"
  },
    {
      title: "Project Id",
      dataIndex: "id",
      key: "id",
      render: (_, record) => (
        <Link to={`${record.id}/${paths.authenticated.AUDIENCES}`}>{record.id}</Link>
      )
    }, {
    title: "Order Number",
    dataIndex: "order_number",
    key: "order_number"
  }, {
    title: "Description",
    dataIndex: "description",
    key: "description",
    render: (_, record) => (
      <ProjectDescription value={record.description} projectId={record.id} accountNumber={accountNumber}/>
    )
  }, {
    title: "Audiences",
    dataIndex: "audiences",
    key: "audiences"
  })
}

export interface IProjectsTableProps {
  dataSource: Array<IProjectsTableData>
  loading: boolean,
  userId: number,
  accountNumber: number
}

export function ProjectsTable({
  userId, 
  accountNumber,
  loading,
  ...rest
}: IProjectsTableProps) {
  
  const { mutateAsync: toggleFavorite, isPending: toggleIsPending } = useToggleFavoriteProject(userId, accountNumber)
  
  const columns = getColumns({ toggleFavorite, accountNumber })
  const tableProps = { ...rest, columns, pagination: false, loading: loading || toggleIsPending }

  return <AxTable {...tableProps} />
}